// components/FeatureIcon.tsx
import React from 'react';
import { Feature } from '../types';
import topCampsiteFeatures from '../utils/campsiteFeatures';
import { FaCampground } from 'react-icons/fa';

interface FeatureIconProps {
  feature: Feature;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

// Type guard to ensure the feature has a description
function isFeatureWithDesc(feature: Feature): feature is Feature & { desc: string } {
  return typeof feature.desc === 'string';
}

// Helper to get color based on feature type
const getFeatureColor = (featureName: string): string => {
  const name = featureName.toLowerCase();
  
  // Water-related features
  if (name.includes('water') || name.includes('shower') || name.includes('pool') || 
      name.includes('swimming') || name.includes('kayak') || name.includes('river') ||
      name.includes('lake') || name.includes('boat') || name.includes('fish')) {
    return 'text-blue-500';
  }
  
  // Nature-related features
  if (name.includes('nature') || name.includes('trail') || name.includes('hik') || 
      name.includes('mountain') || name.includes('wildlife') || name.includes('tree')) {
    return 'text-green-700';
  }
  
  // Facility features
  if (name.includes('toilet') || name.includes('dump') || name.includes('shower') || 
      name.includes('laundry')) {
    return 'text-teal-600';
  }
  
  // Comfort features
  if (name.includes('wifi') || name.includes('power') || name.includes('electric') || 
      name.includes('cabin') || name.includes('kitchen')) {
    return 'text-indigo-500';
  }
  
  // Cost-related features
  if (name.includes('free') || name.includes('fee') || name.includes('cost') || 
      name.includes('dollar')) {
    return name.includes('free') ? 'text-green-600' : 'text-orange-500';
  }
  
  // Fire-related features
  if (name.includes('fire') || name.includes('bbq')) {
    return 'text-red-500';
  }
  
  // Pet features
  if (name.includes('pet') || name.includes('dog')) {
    return 'text-amber-600';
  }
  
  // Default color
  return 'text-gray-600';
};

const FeatureIcon: React.FC<FeatureIconProps> = ({ 
  feature, 
  className = '', 
  size = 'md' 
}) => {
  if (!isFeatureWithDesc(feature)) return null;
  
  const iconSize = size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-xl' : 'text-base';
  
  // Find the exact match in the features list
  let matchedFeature = topCampsiteFeatures.find(
    f => f.name.toLowerCase() === feature.desc.toLowerCase()
  );
  
  // If no exact match, try partial matching for more flexible icon assignment
  if (!matchedFeature) {
    const featureDesc = feature.desc.toLowerCase();
    
    // Try to find a partial match by keywords
    matchedFeature = topCampsiteFeatures.find(f => {
      const name = f.name.toLowerCase();
      
      // Check for common keywords
      if (featureDesc.includes(name) || name.includes(featureDesc)) {
        return true;
      }
      
      // Check for specific feature mappings that aren't direct matches
      if ((featureDesc.includes('water') && name.includes('water')) ||
          (featureDesc.includes('shower') && name.includes('shower')) ||
          (featureDesc.includes('toilet') && name.includes('toilet')) ||
          (featureDesc.includes('campfire') && name.includes('fire')) ||
          (featureDesc.includes('pet') && name.includes('pet')) ||
          (featureDesc.includes('dog') && name.includes('dog')) ||
          (featureDesc.includes('free') && name.includes('free')) ||
          (featureDesc.includes('boat') && name.includes('boat')) ||
          (featureDesc.includes('caravan') && name.includes('caravan')) ||
          (featureDesc.includes('swimming') && name.includes('swim'))) {
        return true;
      }
      
      return false;
    });
  }
  
  if (matchedFeature) {
    // Extract the Icon component from the matched feature
    const IconComponent = matchedFeature.Icon;
    const colorClass = className.includes('text-') ? className : getFeatureColor(matchedFeature.name);
    
    return (
      <IconComponent 
        title={feature.desc} 
        className={`${iconSize} ${colorClass} ${className}`} 
      />
    );
  }
  
  // If no matching feature is found, return a default icon
  const defaultColorClass = className.includes('text-') ? className : getFeatureColor(feature.desc);
  return (
    <FaCampground 
      title={feature.desc} 
      className={`${iconSize} ${defaultColorClass} ${className}`} 
    />
  );
};

export default FeatureIcon;